import { useParams, Link } from "react-router-dom";

const PAGES = {
  faq: {
    overline: "Customer",
    title: "Frequently Asked Questions",
    intro: "Answers to the questions we hear most often.",
    sections: [
      { h: "When will my order arrive?", b: "Every DTA garment is made to order. Please allow 5–8 working days for production, plus shipping. UK delivery normally arrives within 10 working days of ordering." },
      { h: "What sizes do you offer?", b: "Most core garments are offered in XS to XXL. See our size guide for detailed measurements." },
      { h: "Do you ship internationally?", b: "Yes. Rates are calculated at checkout. Customs charges are the responsibility of the recipient." },
      { h: "Can I return a made-to-order product?", b: "Yes. You can return any garment within 30 days of receipt, provided it is unworn and in its original condition. See our returns policy." },
      { h: "Is DTA a mental-health service?", b: "No. DTA supports open conversations around resilience and mental health but does not provide medical advice, diagnosis or crisis support. Please see our Support page for signposting." },
    ],
  },
  delivery: {
    overline: "Customer",
    title: "Delivery",
    intro: "Every DTA garment is produced specifically for your order. This reduces waste and lets us launch carefully. Delivery estimates account for both production and shipping.",
    sections: [
      { h: "UK Standard", b: "£4.95. 5–8 working days production + 2–3 days shipping. Free over £75." },
      { h: "UK Express", b: "£9.95. 5–8 working days production + next working day shipping." },
      { h: "Europe", b: "From £12.95. Calculated at checkout. 5–14 working days." },
      { h: "Rest of World", b: "From £17.95. Calculated at checkout. Delivery times vary by destination." },
    ],
  },
  returns: {
    overline: "Customer",
    title: "Returns & Refunds",
    intro: "We accept returns of unworn products within 30 days of receipt.",
    sections: [
      { h: "How to return", b: "Contact contact@downtestadjust.com with your order number. We will provide return instructions and, where appropriate, a returns label." },
      { h: "Refunds", b: "Refunds are processed within 7 working days of receipt of the returned item, to your original payment method." },
      { h: "Exchanges", b: "For an exchange, please return the original item and place a new order for the correct size." },
      { h: "Exceptions", b: "For hygiene reasons, headwear and undergarments cannot be returned once opened." },
    ],
  },
  "size-guide": {
    overline: "Customer",
    title: "Size Guide",
    intro: "Measurements are in centimetres. If you are between sizes, we recommend sizing up for hoodies and staying true for T-shirts.",
    sections: [
      { h: "T-Shirts (Chest / Length)", b: "XS 48/68 · S 51/70 · M 54/72 · L 57/74 · XL 60/76 · XXL 63/78" },
      { h: "Hoodies & Sweatshirts (Chest / Length)", b: "S 55/70 · M 58/72 · L 61/74 · XL 64/76 · XXL 67/78" },
      { h: "Headwear", b: "One size. Adjustable strap fits 56–62cm circumference." },
    ],
  },
  "garment-care": {
    overline: "Customer",
    title: "Garment Care",
    intro: "Look after your DTA garments and they will last far longer than the average print-on-demand product.",
    sections: [
      { h: "Wash", b: "Machine wash cold (30°C or below). Wash inside out to protect prints and embroidery." },
      { h: "Dry", b: "Do not tumble dry. Line dry away from direct sunlight." },
      { h: "Iron", b: "Cool iron reverse only. Do not iron directly on printed graphics." },
      { h: "Store", b: "Fold heavy hoodies rather than hanging to preserve shape." },
    ],
  },
  privacy: {
    overline: "Legal",
    title: "Privacy Policy",
    intro: "This policy explains how DTA collects, uses and safeguards personal data under UK GDPR.",
    sections: [
      { h: "Data we collect", b: "Name, email address, shipping address, order history, IP-based analytics, marketing consent." },
      { h: "How we use it", b: "To fulfil orders, communicate about purchases, provide customer support and, with consent, send marketing emails." },
      { h: "Your rights", b: "You may request access, correction or deletion of your data at any time by emailing contact@downtestadjust.com." },
      { h: "Third parties", b: "Payment providers, print-on-demand fulfilment partners and analytics providers. We share only what is required." },
    ],
  },
  cookies: {
    overline: "Legal",
    title: "Cookie Policy",
    intro: "We use cookies to power essential site functionality and, with your consent, analytics and advertising.",
    sections: [
      { h: "Essential", b: "Cookies required for the basket, checkout and customer account. These cannot be disabled." },
      { h: "Analytics", b: "Optional cookies that help us understand which pages perform well. You can opt out." },
      { h: "Marketing", b: "Optional cookies for Meta, TikTok and other advertising platforms. You can opt out." },
    ],
  },
  terms: {
    overline: "Legal",
    title: "Terms & Conditions",
    intro: "By placing an order with DTA you agree to the following terms.",
    sections: [
      { h: "Made to order", b: "Products are produced specifically for your order. Once production has begun a cancellation cannot always be actioned." },
      { h: "Pricing", b: "All prices are shown in GBP and include UK VAT where applicable." },
      { h: "Intellectual property", b: "All DTA branding, wordmarks and content remain the intellectual property of DTA." },
    ],
  },
  "mental-health": {
    overline: "Safeguarding",
    title: "Mental-Health Disclaimer",
    intro: "DTA promotes positive conversations around resilience and mental health but does not provide medical services.",
    sections: [
      { h: "Not medical advice", b: "Information on this website is provided for signposting purposes only. It is not a substitute for professional advice, diagnosis or treatment." },
      { h: "Crisis support", b: "If you or someone else is in immediate danger, contact 999 (UK) or Samaritans on 116 123." },
      { h: "Personal stories", b: "Any personal stories published on this website are shared with informed consent and reviewed against safeguarding guidelines." },
    ],
  },
};

function renderWithEmails(text) {
  const re = /([\w.+-]+@[\w-]+\.[\w.-]+)/g;
  const parts = text.split(re);
  return parts.map((part, i) => {
    if (part && part.includes("@") && /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(part)) {
      return (
        <a
          key={i}
          href={`mailto:${part}`}
          className="text-[#031D47] underline underline-offset-2 hover:opacity-80"
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

export default function InfoPage() {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page) {
    return (
      <div className="dta-container py-40 text-center">
        <p className="dta-overline text-navy">404</p>
        <h1 className="section-heading text-4xl mt-3">Page not found.</h1>
        <Link to="/" className="dta-btn dta-btn-dark mt-8 inline-flex">Return home</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#F4F3EF]">
      <section className="bg-[#0A0A0A] text-white py-24 md:py-28">
        <div className="dta-container">
          <p className="dta-overline text-white/60">{page.overline}</p>
          <h1 className="hero-headline text-4xl md:text-7xl mt-4 max-w-3xl">{page.title}</h1>
          <p className="mt-6 text-white/70 max-w-2xl">{page.intro}</p>
        </div>
      </section>
      <section className="py-16 md:py-24">
        <div className="dta-container grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="dta-overline text-navy sticky top-28">On this page</div>
          </div>
          <div className="md:col-span-8 space-y-8 max-w-2xl">
            {page.sections.map((s, i) => (
              <div key={i} className="border-t border-black/10 pt-6">
                <h2 className="font-display uppercase text-2xl md:text-3xl font-bold">{s.h}</h2>
                <p className="mt-3 text-[#272727] leading-relaxed">{renderWithEmails(s.b)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
